import React, { useEffect, useMemo, useState } from 'react';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { School } from 'models/school';
import { getAllSchoolUnionFiles, getSchoolUnionFileCategories, SchoolUnionFile, SchoolUnionFileCategory } from 'services/schoolUnion/files';
import Loader from 'components/loader';
import './Files.scss';
import './FilesCategories.scss';

interface Props { school: School; }
const VISIBLE_CATEGORIES = 8;

function extension(url: string) {
   const result = String(url || '').split('?')[0].split('.').pop();
   return result && result.length <= 5 ? result.toUpperCase() : 'FILE';
}

export default function Files({ school }: Props) {
   const [items, setItems] = useState<SchoolUnionFile[]>([]);
   const [categories, setCategories] = useState<SchoolUnionFileCategory[]>([]);
   const [category, setCategory] = useState('ALL');
   const [query, setQuery] = useState('');
   const [loading, setLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);
   const [showAllCategories, setShowAllCategories] = useState(false);

   useEffect(() => {
      Promise.all([getSchoolUnionFileCategories(school.id), getAllSchoolUnionFiles(school.id, {})])
         .then(([nextCategories, nextItems]) => { setCategories(nextCategories || []); setItems(nextItems || []); })
         .catch(() => { setCategories([]); setItems([]); setLoadError(true); })
         .finally(() => setLoading(false));
   }, [school.id]);

   const visible = useMemo(() => items.filter(item =>
      (category === 'ALL' || item.category === category) && item.title.toLowerCase().includes(query.trim().toLowerCase())
   ), [category, items, query]);
   const firstCategories = categories.slice(0, VISIBLE_CATEGORIES);
   const categoryChips = showAllCategories || categories.length <= VISIBLE_CATEGORIES
      ? categories
      : [...firstCategories, ...categories.filter(item => item.key === category && !firstCategories.some(visibleItem => visibleItem.key === item.key))];
   const groups = useMemo(() => {
      const known = categories.map(item => ({ ...item, files: visible.filter(file => file.category === item.key) }));
      const knownKeys = new Set(categories.map(item => item.key));
      const uncategorised = visible.filter(file => !file.category || !knownKeys.has(file.category));
      return uncategorised.length ? [...known, { id: 'OTHER', key: 'OTHER', name: 'Other resources', files: uncategorised }] : known;
   }, [categories, visible]);
   if (loading) return <Loader />;

   return <main className='union-resources'>
      <header><div><span>Information library</span><h1>Resources</h1><p>Documents, guidance and downloads from {school.name}.</p></div><strong>{items.length} files</strong></header>
      {items.length > 0 && <section className='union-resources__filters'>
         <label><SearchRoundedIcon /><input value={query} onChange={event => setQuery(event.target.value)} placeholder='Search resources' /></label>
         <nav className='union-resources__categories' aria-label='Resource categories'><button type='button' className={category === 'ALL' ? 'is-active' : ''} onClick={() => setCategory('ALL')}>All resources</button>{categoryChips.map(item => <button type='button' className={category === item.key ? 'is-active' : ''} onClick={() => setCategory(item.key)} key={item.id || item.key}>{item.name}</button>)}{categories.length > VISIBLE_CATEGORIES && <button type='button' className='is-more' onClick={() => setShowAllCategories(value => !value)}>{showAllCategories ? 'Show fewer' : `Show ${categories.length - VISIBLE_CATEGORIES} more`}<span aria-hidden='true'>{showAllCategories ? '−' : '+'}</span></button>}</nav>
      </section>}
      {!loadError && visible.length > 0 ? <div className='union-resources__sections'>{groups.map(group => {
         const files = group.files;
         if (!files.length) return null;
         return <section key={group.id || group.key}><div className='union-resources__title'><FolderOpenRoundedIcon /><h2>{group.name}</h2><span>{files.length}</span></div><div className='union-resources__grid'>{files.map(file => <a key={file.id} href={file.url} target='_blank' rel='noreferrer'><div className='union-resources__icon'><InsertDriveFileOutlinedIcon /><small>{extension(file.url)}</small></div><div><h3>{file.title}</h3><p>Open or download this resource</p></div><DownloadRoundedIcon className='union-resources__download' /></a>)}</div></section>;
      })}</div> : <section className='union-resources__empty' role={loadError ? 'alert' : undefined}><FolderOpenRoundedIcon /><h2>{loadError ? 'Resources could not be loaded' : items.length ? 'No matching resources' : 'No resources published yet'}</h2><p>{loadError ? 'Please refresh the page and try again.' : items.length ? 'Try a different search or category.' : 'Documents and useful links will appear here when published.'}</p></section>}
   </main>;
}
